/** `salary_run.rate_breakdown` as the client reads it: pure and importless.
 *  ⛔ Separate from `payroll.ts` on purpose: that file imports `./api` -> `teams.ts` -> `msal.ts`, which reads `window.location.origin` at module scope, so a node-environment test importing it fails at collection.
 *  `payroll.ts` re-exports the two types below. Every stored segment carries the Power App export's capitalisation (`Rate`, `Gross`, `TotalHours`...), which `toFrozenSegment` resolves once. */

/** One day inside a frozen segment's span that the segment did not pay for, derived server-side at read time.
 *  `reason` is a code and is translated (`payroll.excluded_<reason>`); `label` is a server-written string and is not. `null` on `weekend` and `unaccounted`. */
export interface ExcludedDay {
  day: string
  reason: 'holiday' | 'weekend' | 'leave' | 'work_mode' | 'not_approved' | 'unaccounted'
  label: string | null
}

/** One rate period as frozen at issue; a separate type from `PaySegment` because the four keys the issue call leaves out are not recoverable later.
 *  Figures arrive as strings on a run this application issued and as JSON numbers on an imported one: read them through `Number()`.
 *  ⚠ The six figure fields are optional on purpose: an advance run's ledger-only segment has no rate, span or basis, and `undefined` must not render as a zero (see `isLedgerOnly`, `formatMoneyOrDash`).
 *  `model` is resolved once at the fetch boundary, never read from `type`/`Type` in a component; it is `null` on ledger-only segments AND on imported segments whose cell held a JSON null, so `isLedgerOnly` cannot be `model == null`. */
export interface FrozenSegment {
  /** `null` when the wire has neither `type` nor `Type`: render as cannot-tell, never through `t()` with a fallback, which would print the word "Model" as a pay basis. */
  model: string | null
  /** Absent on a ledger-only segment, where there was never one to record. */
  rate?: string
  start?: string
  end?: string
  hours?: string
  shifts?: string
  gross?: string
  /** @see PaySegment.carry_over */
  carry_over?: boolean
  /** The dates this segment paid for. Absent on rows issued before carry-over reconciliation and on imported rows: "cannot tell", not "nothing was paid". */
  days?: string[]
  shift_days?: string[]
  /** Weekday calendar holidays this segment paid the 8-hour credit for; already inside `hours` and the gross, deliberately not in `days`.
   *  Absent on an earlier run; `[]` when nothing was credited. @see holidayCredit */
  holiday_days?: string[] | null
  // Added to the same JSONB column later, so `undefined`/`null` means issued before this itemisation shipped, not "none".
  allowance?: string | null
  allowance_detail?: Record<string, string> | null
  /** Day count by work mode including non-earning ones; sums to `days!.length` on the same segment. */
  mode_days?: Record<string, number> | null
  /** Paid leave (CL, PSL) days by `leave_type`. AL has its own `al_days` on the row. */
  leave_detail?: Record<string, string> | null
  /** Hours by type as LOGGED. Not the parts of the weighted `hours` above, so they do not sum to it. Machine keys: translate before display or a payslip prints "over_night". */
  hour_detail?: Record<string, string> | null
  transit_add?: boolean | null
  /** Days the span covers that the `days` ledger did not pay for, each with why. `null`/absent, NOT `[]`, when the difference cannot be taken (no `days` or no span);
   *  `[]` means paid in full. Read through `recordedList` so the two never render alike. @see ExcludedDay */
  excluded_days?: ExcludedDay[] | null
  /** Task work frozen with the segment. `undefined` means issued before task pay was itemised and renders NO task row, never a zero. */
  tasks?: string
  task_pay?: string
  /** One entry per priced day when a rule set priced the segment; empty on the flat path. @see TaskDayDetail */
  task_detail?: TaskDayDetail[] | null
  /** Free text the operator typed on the set ("box"). Data, never translated. */
  task_unit_label?: string | null
  task_rule_set_name?: string | null
}

/** One day's task pricing: the day's count picked ONE tier (`min_count`) and the whole count was paid at its `rate`. */
interface TaskDayDetail {
  day: string
  count: string
  min_count: string
  rate: string
  pay: string
}

/** The wire's `task_detail` entry: figures arrive as strings or numbers. */
interface TaskDayDetailWire {
  day: string
  count: string | number
  min_count: string | number
  rate: string | number
  pay: string | number
}

/** The wire shape of one `rate_breakdown` entry.
 *  ⛔ Exported only for `payroll.ts`'s raw response types and deliberately not re-exported from it, so the capitalised spellings stay out of components.
 *  The routes declare `-> Any`, so nothing server-side validates `rate_breakdown`: the wire is exactly the JSONB column. */
export interface FrozenSegmentWire
  extends Omit<
    FrozenSegment,
    'model' | 'rate' | 'start' | 'end' | 'hours' | 'shifts' | 'gross' | 'tasks' | 'task_pay' | 'task_detail'
  > {
  tasks?: string | number
  task_pay?: string | number
  task_detail?: TaskDayDetailWire[] | null
  type?: string | null
  rate?: string | number
  start?: string
  end?: string
  hours?: string | number
  shifts?: string | number
  gross?: string | number
  /** The export's spellings. `No`, `AddValue` and `Task`/`TaskPay`/`TaskSalary` are deliberately not mapped; see `toFrozenSegment`. */
  Type?: string | null
  Rate?: string | number
  Start?: string
  End?: string
  TotalHours?: string | number
  TotalShift?: string | number
  Gross?: string | number
}

/** One figure from whichever spelling carries it, as a string; `undefined` when neither does.
 *  ⛔ Not `?? ''`: an empty string reads as 0 through `Number()` and a segment with no rate would print `0.00`. A real `0` must survive as `'0'`, so test `== null`, never falsiness. */
function figure(
  lower: string | number | undefined,
  upper: string | number | undefined,
): string | undefined {
  const value = lower ?? upper
  return value == null ? undefined : String(value)
}

/** The wire's two spellings resolved to one shape, once, at the fetch boundary. `hours`/`TotalHours` and `shifts`/`TotalShift` are renames, not case changes.
 *  ⛔ Four export keys are deliberately not mapped: `No` (unread); `AddValue` (mapping it onto `allowance` would make honest not-itemised segments claim a recorded total); `Task`/`TaskPay`/`TaskSalary` (no field, different shape). */
export function toFrozenSegment(raw: FrozenSegmentWire): FrozenSegment {
  const {
    type,
    Type,
    rate,
    Rate,
    start,
    Start,
    end,
    End,
    hours,
    TotalHours,
    shifts,
    TotalShift,
    gross,
    Gross,
    tasks,
    task_pay,
    task_detail,
    ...rest
  } = raw
  return {
    ...rest,
    // `??` and not `||`: a JSON `null` basis must stay `null` and render as "cannot tell", never as the word "Model".
    model: type ?? Type ?? null,
    rate: figure(rate, Rate),
    start: start ?? Start,
    end: end ?? End,
    hours: figure(hours, TotalHours),
    shifts: figure(shifts, TotalShift),
    gross: figure(gross, Gross),
    // `undefined` stays `undefined` (never recorded); a real `0` stays '0'.
    tasks: figure(tasks, undefined),
    task_pay: figure(task_pay, undefined),
    task_detail:
      task_detail == null
        ? task_detail
        : task_detail.map((d) => ({
            day: d.day,
            count: String(d.count),
            min_count: String(d.min_count),
            rate: String(d.rate),
            pay: String(d.pay),
          })),
  }
}

/** Whether this segment is an advance run's ledger of days and NOT a rate period (`{days, shift_days}` and nothing else).
 *  ⛔ It has no rate by construction and one must never be supplied: the export's rate describes the FINAL run, so copying or recomputing one would put an unpaid figure on a payslip.
 *  ⛔ Not `model == null` (imported segments with a null basis carry a real rate) and not key count: the test is the absence of a rate AND of a span, on the mapped object. */
export function isLedgerOnly(segment: FrozenSegment): boolean {
  return (
    segment.model == null && segment.rate == null && segment.start == null && segment.end == null
  )
}

/** The dates credited 8 hours each, as their own payslip line, or `null` when there is none to show. DISPLAY ONLY: the hours are already inside `hours` and the gross, and no money is derived here.
 *  `null` for both an absent list and an empty one: neither asserts a fact. */
export function holidayCredit(
  segment: Pick<FrozenSegment, 'holiday_days'>,
): string[] | null {
  const dates = segment.holiday_days
  return dates == null || dates.length === 0 ? null : dates
}
